import { useGetAccountQuery } from "@/shared/services";

export interface HocuspocusGate {
  // Resolved value of the `collaboration` runtime feature flag (org-keyed via the
  // CDN flag config). All-off default until the flag config resolves.
  enabled: boolean;
  // The account query has SETTLED (success or error), so `enabled` is final.
  // Until this is true the flag is unknown — callers must treat the editor as
  // still loading, NOT as "Hocuspocus disabled". useGetAccountQuery is a plain
  // (non-suspense) query, so on a hard reload / cold cache `data` is undefined
  // for the first renders; collapsing that to false would boot a beta account
  // into the wrong (legacy broadcast) transport and latch past the Yjs sync gate.
  ready: boolean;
}

// Runtime gate for Hocuspocus-Yjs collaboration. Returns both the resolved flag
// AND whether the account query has settled, so callers can defer the
// transport/sync-gate decision until the flag is actually known.
export const useHocuspocusGate = (): HocuspocusGate => {
  const { isSuccess, isError } = useGetAccountQuery();
  return {
    enabled: true,
    // On error we fall back to the safe default (legacy broadcast) and stop
    // blocking — better a working non-Yjs editor than an indefinite hang.
    ready: isSuccess || isError,
  };
};

// Boolean convenience for call sites that only persist/branch on the final value
// (and never render during the cold-cache window) — e.g. publish/autosave, which
// are user/changeId triggered and can't fire before the account resolves.
export const useHocuspocusEnabled = (): boolean => useHocuspocusGate().enabled;
